import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageHeader } from '../context/PageHeaderContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, Shield, Save, HelpCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PAGES_FOR_ROLE } from '../constants/pageRegistry';
import AppWideTriggersCard from '../components/AppWideTriggersCard';

const ROLES = [
  { key: 'affiliate', label: 'Affiliates' },
  { key: 'lead', label: 'Leads' },
  { key: 'supervisor', label: 'Supervisors' },
  { key: 'special', label: 'Special' },
  { key: 'admin', label: 'Admins' },
  // iter153 — Restricted-state (ORB-sourced) users live in their own
  // tab. They authenticate via JWT-ticket SSO owned by ORB and never
  // password-login, so most of the password / MFA / app-wide rotation
  // controls don't apply here. The page renders a focused subset of
  // controls for this tab — see the early-return block in the render
  // body for what's shown.
  { key: 'orb_restricted', label: 'Restricted states' },
];

export default function SecurityRequirementsPage() {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [activeRole, setActiveRole] = useState('affiliate');
  const [policies, setPolicies] = useState({});
  const [dirty, setDirty] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  usePageHeader({
    title: 'Security Requirements',
    rightSlot: (
      <button
        onClick={() => navigate('/admin/help-buttons')}
        className="relative w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center"
        data-testid="sec-req-help-buttons-btn"
        aria-label="Help Buttons"
        title="Help Buttons"
      >
        <HelpCircle className="w-5 h-5" />
      </button>
    ),
  }, []);

  useEffect(() => {
    if (user && user.role !== 'admin') navigate('/');
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/security-policies');
      const map = {};
      (res.data?.policies || []).forEach((p) => { map[p.role] = p; });
      setPolicies(map);
      setDirty({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load');
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const update = (section, key, value) => {
    setPolicies((prev) => ({
      ...prev,
      [activeRole]: {
        ...(prev[activeRole] || {}),
        [section]: { ...(prev[activeRole]?.[section] || {}), [key]: value },
      },
    }));
    setDirty((d) => ({ ...d, [activeRole]: true }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const p = policies[activeRole] || {};
      await api.put(`/admin/security-policies/${activeRole}`, {
        password: p.password,
        auto_logout: p.auto_logout,
        password_rotation: p.password_rotation,
        reauth: p.reauth,
      });
      setDirty((d) => ({ ...d, [activeRole]: false }));
      // iter148 — Notify ReauthGate (and any future listeners) that the
      // policy changed so they invalidate their cached copy.
      try { sessionStorage.removeItem('orb_reauth_policy_cache'); } catch (_) { /* ignore */ }
      try { window.dispatchEvent(new CustomEvent('orb-security-policy-changed', { detail: { role: activeRole } })); } catch (_) { /* ignore */ }
      toast.success('Saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const p = policies[activeRole] || {};
  const pwd = p.password || {};
  const al = p.auto_logout || {};
  const rot = p.password_rotation || {};
  // iter148 — Reauthentication policy block (per-role + per-page).
  const reauth = p.reauth || {};
  const reauthTtlSec = reauth.valid_duration_seconds ?? 900;
  const reauthMinutes = Math.floor(reauthTtlSec / 60);
  const reauthSeconds = reauthTtlSec % 60;
  const reauthPages = useMemo(() => PAGES_FOR_ROLE[activeRole] || [], [activeRole]);
  const reauthPageToggles = reauth.page_toggles || {};

  const updateReauthPage = (pageId, value) => {
    setPolicies((prev) => ({
      ...prev,
      [activeRole]: {
        ...(prev[activeRole] || {}),
        reauth: {
          ...(prev[activeRole]?.reauth || {}),
          page_toggles: { ...(prev[activeRole]?.reauth?.page_toggles || {}), [pageId]: value },
        },
      },
    }));
    setDirty((d) => ({ ...d, [activeRole]: true }));
  };

  // Auto-logout duration helpers (stored as seconds, edited as minutes:seconds)
  const minutes = Math.floor((al.duration_seconds || 0) / 60);
  const seconds = (al.duration_seconds || 0) % 60;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24" data-testid="security-requirements-page">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Role tabs */}
        <div className="flex gap-1 overflow-x-auto pb-1">
          {ROLES.map((r) => (
            <button
              key={r.key}
              onClick={() => setActiveRole(r.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap ${
                activeRole === r.key ? 'bg-[#586D52] text-white' : 'bg-white/60 dark:bg-[#2a2a2a] text-muted-foreground'
              }`}
              data-testid={`role-tab-${r.key}`}
            >
              {r.label}{dirty[r.key] ? ' •' : ''}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-xs text-center py-6 text-muted-foreground">Loading…</p>
        ) : activeRole === 'orb_restricted' ? (
          /*
           * iter153 — Restricted-states tab.
           *
           * ORB-sourced users authenticate via the JWT-ticket SSO
           * flow owned by ORB. They have:
           *   - no Uplift password (auth_source: "orb", password: null)
           *   - no Uplift TOTP secret
           *   - no app-wide rotation flags (excluded server-side by
           *     _users_query_for_role and the auto sweeper)
           *
           * So almost every control on the Affiliate/Lead/etc. tabs
           * is irrelevant. The only things WE can meaningfully
           * enforce from Uplift's side are:
           *   1. JWT-ticket TTL          — how long an ORB ticket
           *      stays valid before redemption. Owned by ORB today
           *      (typically 60s) — we only display it, can't
           *      configure it from here.
           *   2. Inactivity auto-logout  — applies to ORB users too
           *      (they're sitting on a real Uplift Bearer token).
           *   3. Per-page reauthentication — same. If we want to
           *      force re-confirm-via-ORB before viewing certain
           *      pages, this is the lever.
           *
           * For v1 we surface the read-only ORB-controlled bits +
           * a single inactivity-timeout slider. The rest of the
           * security policies for these users live in ORB.
           */
          <>
            <Card>
              <CardContent className="p-4 space-y-3" data-testid="orb-restricted-info-card">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">ORB-controlled credentials</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Users in restricted states authenticate via your ORB community using a
                  short-lived signed ticket. Their password, MFA, and rotation policies are
                  controlled by ORB — Uplift never sees their credentials. App-wide
                  password/MFA rotation triggers and the auto-rotation sweeper EXCLUDE
                  these accounts automatically.
                </p>
                <div className="grid grid-cols-1 gap-2 mt-3 text-xs">
                  <div className="flex justify-between bg-white/60 dark:bg-[#2a2a2a] rounded-md px-3 py-2">
                    <span className="text-muted-foreground">Authentication method</span>
                    <span className="font-mono font-medium">ORB JWT ticket (HS256)</span>
                  </div>
                  <div className="flex justify-between bg-white/60 dark:bg-[#2a2a2a] rounded-md px-3 py-2">
                    <span className="text-muted-foreground">Ticket TTL</span>
                    <span className="font-mono font-medium">60s (ORB-owned)</span>
                  </div>
                  <div className="flex justify-between bg-white/60 dark:bg-[#2a2a2a] rounded-md px-3 py-2">
                    <span className="text-muted-foreground">Password / TOTP</span>
                    <span className="font-mono font-medium text-emerald-700 dark:text-emerald-400">Not stored</span>
                  </div>
                  <div className="flex justify-between bg-white/60 dark:bg-[#2a2a2a] rounded-md px-3 py-2">
                    <span className="text-muted-foreground">App-wide rotation triggers</span>
                    <span className="font-mono font-medium text-emerald-700 dark:text-emerald-400">Exempt</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Auto-logout (the one knob we CAN still set for ORB users) */}
            <Card>
              <CardContent className="p-4 space-y-3" data-testid="orb-restricted-auto-logout-card">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Inactivity auto-logout</p>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Even though ORB owns credentials, the Bearer token issued at ticket
                  redemption lives on the user's device. Setting an inactivity timeout
                  forces re-redemption via ORB after the window lapses.
                </p>
                <div className="flex items-center justify-between mt-2">
                  <Label className="text-xs">Enabled</Label>
                  <Switch
                    checked={!!(policies.orb_restricted?.auto_logout?.enabled)}
                    onCheckedChange={(v) => update('auto_logout', 'enabled', !!v)}
                    data-testid="orb-restricted-auto-logout-toggle"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Minutes</Label>
                    <Input
                      type="number"
                      min={0}
                      value={Math.floor((policies.orb_restricted?.auto_logout?.duration_seconds || 0) / 60)}
                      onChange={(e) => {
                        const mins = parseInt(e.target.value || '0', 10);
                        const secs = (policies.orb_restricted?.auto_logout?.duration_seconds || 0) % 60;
                        update('auto_logout', 'duration_seconds', mins * 60 + secs);
                      }}
                      data-testid="orb-restricted-auto-logout-minutes"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Seconds</Label>
                    <Input
                      type="number"
                      min={0}
                      max={59}
                      value={(policies.orb_restricted?.auto_logout?.duration_seconds || 0) % 60}
                      onChange={(e) => {
                        const secs = parseInt(e.target.value || '0', 10);
                        const mins = Math.floor((policies.orb_restricted?.auto_logout?.duration_seconds || 0) / 60);
                        update('auto_logout', 'duration_seconds', mins * 60 + secs);
                      }}
                      data-testid="orb-restricted-auto-logout-seconds"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Save button (reuses the same dirty-tracking + save handler) */}
            <div className="flex justify-end pt-1">
              <Button
                size="sm"
                className="bg-[#586D52] hover:bg-[#475a43] text-white"
                disabled={!dirty.orb_restricted || saving}
                onClick={() => save('orb_restricted')}
                data-testid="orb-restricted-save-btn"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : <Save className="w-3 h-3 mr-2" />}
                Save
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Password */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password</p>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Length</Label>
                    <Input
                      type="number"
                      min={1}
                      value={pwd.min_length ?? ''}
                      onChange={(e) => update('password', 'min_length', parseInt(e.target.value || '0', 10))}
                      data-testid="pw-min-length"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Special</Label>
                    <Input
                      type="number"
                      min={0}
                      value={pwd.min_special_chars ?? ''}
                      onChange={(e) => update('password', 'min_special_chars', parseInt(e.target.value || '0', 10))}
                      data-testid="pw-min-special"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Numbers</Label>
                    <Input
                      type="number"
                      min={0}
                      value={pwd.min_numbers ?? ''}
                      onChange={(e) => update('password', 'min_numbers', parseInt(e.target.value || '0', 10))}
                      data-testid="pw-min-numbers"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Auto-logout */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Auto Logout</p>
                  <Switch
                    checked={!!al.enabled}
                    onCheckedChange={(v) => update('auto_logout', 'enabled', v)}
                    data-testid="auto-logout-toggle"
                  />
                </div>
                {al.enabled && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Minutes</Label>
                      <Input
                        type="number"
                        min={0}
                        value={minutes}
                        onChange={(e) => update('auto_logout', 'duration_seconds', (parseInt(e.target.value || '0', 10) * 60) + seconds)}
                        data-testid="auto-logout-min"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Seconds</Label>
                      <Input
                        type="number"
                        min={0}
                        max={59}
                        value={seconds}
                        onChange={(e) => update('auto_logout', 'duration_seconds', (minutes * 60) + parseInt(e.target.value || '0', 10))}
                        data-testid="auto-logout-sec"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Password rotation (Phase 3 — interval scheduling) */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Auto Password Rotation</p>
                  <Switch
                    checked={!!rot.enabled}
                    onCheckedChange={(v) => update('password_rotation', 'enabled', v)}
                    data-testid="rotation-toggle"
                  />
                </div>
                {rot.enabled && (
                  <div>
                    <Label className="text-xs">Interval (days)</Label>
                    <Input
                      type="number"
                      min={1}
                      value={rot.interval_days ?? ''}
                      onChange={(e) => update('password_rotation', 'interval_days', parseInt(e.target.value || '0', 10))}
                      data-testid="rotation-interval"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* iter148 — Reauthentication (per-page) */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reauthentication</p>
                  <Switch
                    checked={!!reauth.enabled}
                    onCheckedChange={(v) => update('reauth', 'enabled', v)}
                    data-testid="reauth-toggle"
                  />
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Forces a password + MFA prompt when this role opens any toggled-on page below. Once a user reauthenticates, every other reauth-protected page in the app is exempt for the duration set here. Pages with their own dedicated reauth gates (e.g. specific admin features) are unaffected.
                </p>
                {reauth.enabled && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Valid for (minutes)</Label>
                        <Input
                          type="number"
                          min={0}
                          value={reauthMinutes}
                          onChange={(e) => update('reauth', 'valid_duration_seconds', (parseInt(e.target.value || '0', 10) * 60) + reauthSeconds)}
                          data-testid="reauth-min"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Valid for (seconds)</Label>
                        <Input
                          type="number"
                          min={0}
                          max={59}
                          value={reauthSeconds}
                          onChange={(e) => update('reauth', 'valid_duration_seconds', (reauthMinutes * 60) + parseInt(e.target.value || '0', 10))}
                          data-testid="reauth-sec"
                        />
                      </div>
                    </div>
                    <div className="pt-2 border-t border-black/10 dark:border-white/10">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                        Pages requiring reauthentication
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-72 overflow-y-auto pr-1">
                        {reauthPages.map((page) => (
                          <label
                            key={page.id}
                            className="flex items-center justify-between gap-2 p-2 rounded-md bg-white/40 dark:bg-[#1f1f1f]/60 border border-black/5 dark:border-white/5 cursor-pointer"
                            data-testid={`reauth-page-row-${page.id}`}
                          >
                            <span className="text-xs truncate">{page.label}</span>
                            <Switch
                              checked={!!reauthPageToggles[page.id]}
                              onCheckedChange={(v) => updateReauthPage(page.id, v)}
                              data-testid={`reauth-page-toggle-${page.id}`}
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* App-wide reset triggers — Phase 2 (live). */}
            <AppWideTriggersCard />

            <Button
              className="w-full bg-[#586D52] hover:bg-[#475a43]"
              onClick={save}
              disabled={saving || !dirty[activeRole]}
              data-testid="sec-req-save"
            >
              <Save className="w-3.5 h-3.5 mr-1.5" />
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
